import { PALETTE } from '@overlayqa/shared';
import { IntegrationCard } from './IntegrationCard';
import { IntegrationTile } from './integrationLogos';
import { useState } from 'react';
import { useIntegration, useConnectNotion, useDisconnectNotion } from '../../hooks/useIntegrations';
import { useIntegrationAvailability } from '../../hooks/useIntegrationAvailability';
import { log } from '@/lib/logger';

log.info('[NotionIntegrationCard] Module loading...');

export function NotionIntegrationCard() {
  const { integration, isConnected, isLoading, refetch } = useIntegration('notion');
  const { available, reason: unavailableReason } = useIntegrationAvailability('notion');
  const connectMutation = useConnectNotion();
  const disconnectMutation = useDisconnectNotion();

  const [actionError, setActionError] = useState<string | null>(null);

  const isConnecting = connectMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;

  const handleConnect = () => {
    // Never fire a connect the backend can't complete — it's a guaranteed 500.
    if (!available) return;
    setActionError(null);
    connectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to connect Notion. Try again.');
      },
    });
  };

  const handleDisconnect = () => {
    setActionError(null);
    disconnectMutation.mutate(undefined, {
      onError: (err) => {
        setActionError(err.message || 'Failed to disconnect Notion. Try again.');
      },
    });
  };

  return (
    <IntegrationCard
      provider="notion"
      loading={isLoading}
      tile={<IntegrationTile outlined><NotionIcon /></IntegrationTile>}
      name="Notion"
      description="Push captured issues to a Notion database with screenshots and context."
      connection={{
        connected: isConnected,
        connecting: isConnecting,
        disconnecting: isDisconnecting,
        onConnect: handleConnect,
        onDisconnect: handleDisconnect,
        onRetry: () => void refetch(),
        available,
        unavailableReason,
        connectedAt: integration?.createdAt,
        rows: [['Workspace', integration?.providerMetadata?.workspaceName]],
      }}
      errors={[actionError]}
    />
  );
}

function NotionIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M6.017 4.313l55.333-4.087c6.797-.583 8.543-.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277-1.553 6.807-6.99 7.193L24.467 99.967c-4.08.193-6.023-.39-8.16-3.113L3.3 79.94c-2.333-3.113-3.3-5.443-3.3-8.167V11.113c0-3.497 1.553-6.413 6.017-6.8z" fill={PALETTE.surface.card}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M61.35.227L6.017 4.313C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723.967 5.053 3.3 8.167l12.81 16.913c2.137 2.723 4.08 3.307 8.16 3.113L88.723 96.08c5.437-.387 6.99-2.917 6.99-7.193V20.64c0-2.21-.85-2.847-3.443-4.733L74.167 3.14C69.893.14 68.147-.357 61.35.227zM25.694 19.267c-5.52.333-6.78.41-9.93-2.2L8.92 11.467c-.97-.807-.583-1.75 1.17-1.943l52.33-3.887c4.47-.387 6.797 1.167 8.543 2.527l8.447 6.217c.387.193 1.36 1.36.193 1.36l-54.13 3.33-.193.193v.003zM19.267 88.54V29.527c0-2.53.777-3.697 3.103-3.893L86 22.187c2.14-.193 3.107 1.167 3.107 3.693v58.82c0 2.527-.387 4.667-3.887 4.86l-60.377 3.5c-3.5.193-5.183-.97-5.577-4.52zM76.873 32.917c.387 1.75 0 3.5-1.75 3.7l-2.917.577v43.363c-2.527 1.36-4.853 2.14-6.797 2.14-3.107 0-3.883-.97-6.217-3.887L40.587 50.633v27.167l6.02 1.363s0 3.5-4.857 3.5l-13.39.777c-.39-.777 0-2.723 1.357-3.11l3.497-.97V42.14l-4.85-.39c-.39-1.75.58-4.277 3.3-4.473l14.367-.967 19.3 29.5V40.5l-5.053-.583c-.39-2.143 1.163-3.7 3.103-3.887l13.997-.777.003-.337z" fill="#000"/>
    </svg>
  );
}

log.info('[NotionIntegrationCard] Module loaded');
